import { faLocationDot, faMessageLines, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    closeWaitlistPanel,
    deleteWaitlistThunk,
    fetchWaitlistCountsThunk,
    getWaitlistThunk,
    saveWaitlistThunk,
    selectWaitlist
} from 'actions/waitlists/WaitlistsActions';
import Marketplace from '@spike/marketplace-model';
import { push } from 'connected-react-router';
import Button from 'components/UI/V3/Button/Button';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import Spinner from 'components/UI/V3/Spinner';
import WaitlistBadges from 'components/Waitlist/WaitlistBadges/WaitlistBadges';
import useWaitlistConversion from 'components/Waitlist/WaitlistConversion/useWaitlistConversion';
import WaitlistClientWindow from 'components/Waitlist/WaitlistClientWindow/WaitlistClientWindow';
import WaitlistClientInfo from 'components/Waitlist/WaitlistEditor/WaitlistClientInfo';
import WaitlistEditor from 'components/Waitlist/WaitlistEditor/WaitlistEditor';
import {
    buildUpdateWaitlistInput,
    convertWaitlistToFormValues,
    WaitlistFormValues
} from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { getWaitlistCustomerName } from 'components/Waitlist/waitlistUtils';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { Waitlist } from 'model/Waitlist';
import moment from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TwoWaySMSPath } from 'routes/Paths';
import { RootState } from 'store';
import styles from './WaitlistDetail.module.scss';

interface WaitlistDetailProps {
    waitlistId: number;
    marketplaceIds: Array<number>;
}

// Comparable snapshot of the editable values, to know when there are unsaved changes.
const toSnapshot = (values: WaitlistFormValues) =>
    JSON.stringify({
        ...values,
        validUntil: values.validUntil?.format('YYYY-MM-DD') ?? null,
        notes: values.notes.trim()
    });

export const WaitlistDetail: FunctionComponent<WaitlistDetailProps> = ({ waitlistId, marketplaceIds }) => {
    const dispatch = useDispatch();

    const waitlist = useSelector<RootState, Waitlist | undefined>(state =>
        state.waitlists.selected?.id === waitlistId ? state.waitlists.selected : undefined
    );

    const currentMarketplace = useSelector<RootState, Marketplace>(state => state.marketplace.marketplace);
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const { convert, converting } = useWaitlistConversion();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showClient, setShowClient] = useState(false);
    const [errors, setErrors] = useState<Array<string>>([]);

    const location = useMemo(() => {
        const organizationLocation = organizationMarketplaces.find(current => current.id === waitlist?.marketplaceId);

        return organizationLocation
            ? { timeZone: organizationLocation.timeZone, schedule: organizationLocation.schedule }
            : { timeZone: currentMarketplace.schedule.timeZone, schedule: currentMarketplace.schedule };
    }, [organizationMarketplaces, currentMarketplace, waitlist?.marketplaceId]);

    const initialValues = useMemo(
        () => (waitlist ? convertWaitlistToFormValues(waitlist, location.timeZone) : undefined),
        [waitlist, location.timeZone]
    );

    const [values, setValues] = useState<WaitlistFormValues | undefined>(initialValues);

    // A saved or reloaded waitlist resets the edits.
    useEffect(() => {
        setValues(initialValues);
        setErrors([]);
    }, [initialValues]);

    useEffect(() => {
        const fetchWaitlist = async () => {
            const found = await (dispatch(getWaitlistThunk(waitlistId)) as unknown as Promise<boolean>);

            if (!found) {
                dispatch(selectWaitlist(undefined));
            }
        };

        fetchWaitlist();
    }, [waitlistId]);

    const backHandler = () => dispatch(selectWaitlist(undefined));

    const messageClientHandler = (customerId: number) => {
        dispatch(closeWaitlistPanel());
        dispatch(push(`${TwoWaySMSPath}?customerId=${customerId}`));
    };

    const deleteHandler = async () => {
        setDeleting(true);

        const deleted = await (dispatch(deleteWaitlistThunk(waitlistId)) as unknown as Promise<boolean>);

        if (deleted) {
            // The panel goes back to the list, which unmounts this detail.
            dispatch(fetchWaitlistCountsThunk(marketplaceIds));
            return;
        }

        setDeleting(false);
    };

    if (waitlist === undefined || values === undefined || initialValues === undefined) {
        return (
            <>
                <DrawerHeader
                    title="Waitlist Detail"
                    onBack={backHandler}
                />
                <DrawerBody className={styles.loading}>
                    <Spinner size={32} />
                </DrawerBody>
            </>
        );
    }

    const canConvert = waitlist.marketplaceId === currentMarketplace.id;
    const dirty = toSnapshot(values) !== toSnapshot(initialValues);
    const serviceCount = values.pets.reduce((count, pet) => count + pet.services.length, 0);

    const saveHandler = async () => {
        setSaving(true);
        setErrors([]);

        const result = await (dispatch(
            saveWaitlistThunk(waitlist.marketplaceId, buildUpdateWaitlistInput(waitlist, values), waitlist.id)
        ) as unknown as Promise<{ errors: Array<string> }>);

        setSaving(false);

        if (result.errors.length > 0) {
            setErrors(result.errors);
            return;
        }

        dispatch(fetchWaitlistCountsThunk(marketplaceIds));
    };

    return (
        <>
            <WaitlistEditor
                title="Waitlist Detail"
                marketplaceId={waitlist.marketplaceId}
                timeZone={location.timeZone ?? moment.tz.guess()}
                schedule={location.schedule}
                values={values}
                errors={errors}
                onBack={backHandler}
                onChange={changes => setValues(current => current && { ...current, ...changes })}
                renderTop={client => (
                    <>
                        <WaitlistBadges waitlist={waitlist} />

                        <p className={styles.location}>
                            <FontAwesomeIcon icon={faLocationDot} /> {waitlist.marketplaceName}
                        </p>

                        <WaitlistClientInfo
                            client={client}
                            fallback={{
                                name: getWaitlistCustomerName(waitlist),
                                phone: waitlist.customerPhone,
                                email: waitlist.customerEmail
                            }}
                            onNameClick={waitlist.customerId ? () => setShowClient(true) : undefined}
                            action={
                                <button
                                    type="button"
                                    className={styles.messageButton}
                                    aria-label="Message client"
                                    onClick={() => messageClientHandler(waitlist.customerId)}
                                >
                                    <FontAwesomeIcon icon={faMessageLines} />
                                </button>
                            }
                        />
                    </>
                )}
                footer={
                    <DrawerFooter className={styles.footer}>
                        <div className={styles.actions}>
                            {dirty ? (
                                <>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="large"
                                        label="Cancel"
                                        disabled={saving}
                                        onClick={() => setValues(initialValues)}
                                    />
                                    <Button
                                        fullWidth
                                        variant="green"
                                        size="large"
                                        label="Save Changes"
                                        disabled={saving || serviceCount === 0}
                                        isLoading={saving}
                                        onClick={saveHandler}
                                    />
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className={styles.deleteButton}
                                        aria-label="Delete waitlist"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                    </button>
                                    <Button
                                        fullWidth
                                        variant="green"
                                        size="large"
                                        label="Convert to Booking"
                                        // Add Appointment books in the current location only.
                                        disabled={!canConvert || converting}
                                        isLoading={converting}
                                        onClick={() => convert(waitlist)}
                                    />
                                </>
                            )}
                        </div>

                        {!dirty && !canConvert && (
                            <p className={styles.switchLocation}>
                                Switch to {waitlist.marketplaceName} to convert this request into a booking.
                            </p>
                        )}
                    </DrawerFooter>
                }
            />

            {showClient && (
                <WaitlistClientWindow
                    viewOnly
                    clientId={waitlist.customerId}
                    onClose={() => setShowClient(false)}
                />
            )}

            {showDeleteModal && (
                <GenericDeleteModal
                    title={<p className={styles.modalTitle}>Delete</p>}
                    body={<p className={styles.modalBody}>Are you sure you want to delete and cancel this waitlist?</p>}
                    confirmLabel="Yes, delete"
                    isLoading={deleting}
                    onConfirm={deleteHandler}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}
        </>
    );
};

export default WaitlistDetail;
